import { AuthenticatedRequestHandler, IAuthenticatedRequestHandlerResponse } from '@api/utils/AuthenticatedRequestHandler'
import HiveClient from 'IhmATHiveService-coral-javascript-clients/jquery-client'
import { EnvironmentDetail, CountMetric, LatencyMetric, Exception } from '@amzn/ihm-associate-tools-js-lib'

export class HiveServiceClient {
    private static _instance: HiveClient
    private readonly client: any
    private readonly endpoint: string

    public static getInstance (): HiveServiceClient {
        if (!HiveServiceClient._instance) {
            HiveServiceClient._instance = new HiveServiceClient()
        }
        return HiveServiceClient._instance
    }

    /**
     * Updates labor move status
     *
     * @param {string} storeId: storeId of the labor move
     * @param {string} siteId: siteId of the labor move
     * @param {string} laborMoveId: id of the labor move to be updated
     * @param {string} status: status used to update the labor move
     *
     * @return {Promise} - promise containing a boolean representing whether or not the update was successful
     * TODO:Convert to Object type if more parameters are added here.
     */
    public async updateLaborMove (storeId: string, siteId: string, laborMoveId: string, status: string): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'UpdateLaborMove/' + storeId,
            method: 'POST',
            body: {
                storeId,
                siteId,
                laborMoveId,
                status,
            }}, 'updateLaborMove')
    }

    /**
     * Acknowledges that a Connections content was received
     *
     * @param {string} storeId: storeId that the user is in
     * @param {string} contentId id of the Connections content
     * @param {string} requestToken requestToken
     * @param {string} offeringId offeringId
     *
     * @return {Promise} - promise containing the response (empty output)
     */
    public async acknowledgeContentReceived (storeId: string, contentId: string, requestToken: string, offeringId: string): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'AcknowledgeContentReceived/' + storeId,
            method: 'POST',
            body: {
                storeId,
                contentId,
                requestToken,
                offeringId,
            }}, 'acknowledgeContentReceived')
    }

    /**
     * Gets scheduled Connection questions for a user
     * @param {string} storeId: storeId that the user is in
     * @param {number} timezoneOffset timezone offset
     *
     * @return {Promise} - promise containing the response of a list of scheduled content and request token
     */
    public async getScheduledPendingContent (storeId: string, timezoneOffset: number): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'GetScheduledPendingContent/' + storeId,
            method: 'POST',
            body: {
                storeId,
                timezoneOffset,
            }}, 'getScheduledPendingContent')
    }

    /**
     * Submits a Connection question response
     * @param {string} storeId: storeId that the user is in
     * @param {string} contentId id of the Connections content
     * @param {string} answerId id of the answer choice
     * @param {string} requestToken request token
     * @param {string} offeringId offeringId
     *
     * @return {Promise} - promise containing the response of a follow up question if applicable
     */
    public async putContentResponse (storeId: string, contentId: string, answerId: string, requestToken: string, offeringId: string): Promise<any> {
        return this.sendRequest({
            url: this.endpoint + 'PutContentResponse/' + storeId,
            method: 'POST',
            body: {
                storeId,
                contentId,
                answerId,
                requestToken,
                offeringId,
            }}, 'putContentResponse')
    }

    /**
     * Private Constructor setting the HiveService client and its endpoint
     * @private
     */
    private constructor () {
        this.client = HiveClient
        this.client.config.overrideDomain(EnvironmentDetail.getDomain())
        this.client.config.overrideRealm(EnvironmentDetail.getRealm())
        this.endpoint = this.client.config.endpoint()
        if (this.endpoint.charAt(this.endpoint.length - 1) !== '/') {
            this.endpoint = this.endpoint + '/'
        }
        console.log('ATBase: HiveServiceClient endpoint: ', this.endpoint)
    }

    /**
     * Makes the call to the abstract authenticatedRequestsClient.
     * @param options call parameters.
     * @returns {*} Output from the call (from the underlying service)
     */
    private async sendRequest (options, requestName) {

        const operationName = 'hive.' + requestName
        const faultMetric = new CountMetric(undefined, 'Fault', 'IhmFlyingDutchman', operationName)
        const error4xxMetric = new CountMetric(undefined, '4xx', 'IhmFlyingDutchman', operationName)
        const error5xxMetric = new CountMetric(undefined, '5xx', 'IhmFlyingDutchman', operationName)
        const latencyMetric = new LatencyMetric(undefined, 'Time', 'IhmFlyingDutchman', operationName)
        latencyMetric.start()

        return AuthenticatedRequestHandler.raw(options).then((response) => {
            latencyMetric.stop()
            if (response.ok) {
                faultMetric.count(0)
                error4xxMetric.count(0)
                error5xxMetric.count(0)

                return response.body
            } else {
                if (response.status >= 400 && response.status < 500) {
                    error4xxMetric.count(1)
                    error5xxMetric.count(0)
                } else if (response.status >= 500) {
                    error5xxMetric.count(1)
                    error4xxMetric.count(0)
                } else {
                    error5xxMetric.count(0)
                    error4xxMetric.count(0)
                }
                faultMetric.count(1)

                return Promise.reject(new Exception({
                    message: 'An error occurred while sending Request for ' + requestName,
                }))
            }
        })
    }
}
